'use client';

import React, { Fragment, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useIsHydrated } from '@/ui/hooks/useIsHydrated';
import { twMerge } from 'tailwind-merge';
import { TileFragmentFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { ArrowLongLeftIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'next-intl';
import TilesArrayRenderer from '../../tileRenderers/PageGridRenderer/tiles-array-renderer';

export default function Drawer({
  children,
  rootClass,
  overlayClose,
  open,
  extraLayerShown,
  extraLayerData,
  setExtraLayerShown,
  afterLeave,
}: Readonly<{
  children: React.ReactNode;
  rootClass: string;
  overlayClose: () => void;
  open: boolean;
  extraLayerShown?: boolean;
  setExtraLayerShown?: (value: boolean) => void;
  extraLayerData?: TileFragmentFragment[];
  afterLeave?: () => void;
}>) {
  const focusRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && focusRef.current) {
      setTimeout(() => {
        focusRef.current?.focus();
      }, 20);
    }
  }, [open]);

  const isHydrated = useIsHydrated();
  const t = useTranslations('FullScreenOverlay');
  return (
    <Transition.Root show={isHydrated && open} as={Fragment} afterLeave={afterLeave}>
      <Dialog
        as="div"
        className={twMerge('relative', rootClass)}
        onClose={overlayClose}
        ref={focusRef}
      >
        <Dialog.Panel
          onTouchMove={(e) => {
            e.stopPropagation();
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
          }}
        >
          <AnimatePresence>
            {extraLayerShown && !!extraLayerData && (
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
                className="fixed inset-y-0 z-70 h-full w-full overflow-auto bg-white md:z-0 md:w-1/2 lg:w-7/12 xl:w-2/3"
              >
                {setExtraLayerShown && (
                  <div className="m-3 flex items-start justify-between sm:m-4">
                    <div className="flex items-center">
                      <button
                        type="button"
                        className="flex items-center gap-2 font-medium"
                        onClick={() => {
                          setExtraLayerShown(false);
                        }}
                      >
                        <ArrowLongLeftIcon className="h-8 w-8 text-primary-100" />
                        {t('Back')}
                      </button>
                    </div>
                  </div>
                )}
                {!!extraLayerData && <TilesArrayRenderer tiles={extraLayerData} />}
              </motion.div>
            )}
          </AnimatePresence>

          <Transition.Child
            as={Fragment}
            enter="transform transition ease-in-out duration-500 sm:duration-700"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transform transition ease-in-out duration-500 sm:duration-700"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <div className="fixed inset-y-0 right-0 w-full md:w-1/2 lg:w-5/12 xl:w-1/3">
              <div className="flex h-full w-full flex-col bg-white shadow-xl">{children}</div>
            </div>
          </Transition.Child>
        </Dialog.Panel>
      </Dialog>
    </Transition.Root>
  );
}
